/**
 * Resolve clinical narrative pronouns from explicit gender and/or patient full name.
 * Mirrors BE/src/utils/inferPronounsFromName.js
 * Supports "First Last", "Last First", and "Last, First" name entry.
 */

const MALE_FIRST_NAMES = new Set([
  "aaron", "adam", "alan", "albert", "alexander", "alfred", "andrew", "anthony", "antonio",
  "arthur", "austin", "benjamin", "billy", "bobby", "bradley", "brandon", "brian", "bruce",
  "carl", "carlos", "charles", "christopher", "clarence", "craig", "daniel", "david", "dennis",
  "donald", "douglas", "earl", "edward", "eugene", "frank", "fred", "gary", "george", "gerald",
  "gregory", "harold", "harry", "henry", "howard", "jack", "james", "jason", "jeffrey", "jeremy",
  "jerry", "jesse", "jimmy", "joe", "john", "johnny", "jonathan", "jose", "joseph", "joshua",
  "juan", "justin", "keith", "kenneth", "kevin", "larry", "lawrence", "louis", "mark", "martin",
  "matthew", "michael", "nicholas", "noah", "patrick", "paul", "peter", "philip", "ralph",
  "randy", "raymond", "richard", "robert", "roger", "ronald", "roy", "russell", "ryan", "samuel",
  "scott", "sean", "stephen", "steven", "terry", "thomas", "timothy", "todd", "victor", "vincent",
  "walter", "wayne", "william", "willie", "zachary", "mohammed", "mohammad", "ahmed", "ali",
  "raj", "amit", "vikram", "miguel", "jorge", "luis", "pedro", "diego", "roberto",
]);

const FEMALE_FIRST_NAMES = new Set([
  "alice", "amanda", "amy", "andrea", "angela", "ann", "anna", "anne", "annie", "ashley",
  "barbara", "betty", "beverly", "brenda", "carol", "carolyn", "catherine", "charlotte", "cheryl",
  "christina", "christine", "cynthia", "deborah", "debra", "denise", "diana", "diane", "donna",
  "doris", "dorothy", "elizabeth", "emily", "emma", "evelyn", "frances", "gloria", "grace",
  "hannah", "heather", "helen", "irene", "jacqueline", "jane", "janet", "janice", "jean",
  "jennifer", "jessica", "joan", "joyce", "judith", "judy", "julia", "julie", "karen", "katherine",
  "kathleen", "kathryn", "kelly", "kimberly", "laura", "lauren", "linda", "lisa", "lori", "louise",
  "margaret", "maria", "marie", "marilyn", "martha", "mary", "megan", "melissa", "michelle",
  "nancy", "natalie", "nicole", "norma", "olivia", "pamela", "patricia", "paula", "phyllis",
  "rachel", "rebecca", "rose", "ruby", "ruth", "samantha", "sandra", "sara", "sarah", "sharon",
  "shirley", "sophia", "stephanie", "susan", "tammy", "teresa", "theresa", "tiffany", "virginia",
  "wanda", "priya", "anita", "sunita", "fatima", "aisha", "sofia", "isabella", "elena", "carmen",
]);

const PRONOUN_SETS = {
  male: {
    pronouns: "he/him",
    pronounSubject: "he",
    pronounObject: "him",
    pronounPossessive: "his",
  },
  female: {
    pronouns: "she/her",
    pronounSubject: "she",
    pronounObject: "her",
    pronounPossessive: "her",
  },
  neutral: {
    pronouns: "they/them",
    pronounSubject: "they",
    pronounObject: "them",
    pronounPossessive: "their",
  },
};

const NAME_SUFFIXES = new Set(["jr", "sr", "ii", "iii", "iv", "md", "phd", "do"]);

function normalizeNameToken(token) {
  return String(token || "")
    .replace(/[.,]/g, "")
    .trim()
    .toLowerCase();
}

function parseNameTokensForInference(name, firstName, lastName) {
  const tokens = [];

  const pushParts = (str) => {
    String(str || "")
      .trim()
      .split(/\s+/)
      .forEach((part) => {
        const n = normalizeNameToken(part);
        if (n && !NAME_SUFFIXES.has(n)) tokens.push(n);
      });
  };

  if (firstName && String(firstName).trim()) pushParts(firstName);
  if (lastName && String(lastName).trim()) pushParts(lastName);

  const raw = String(name || "").trim();
  if (raw) {
    if (raw.includes(",")) {
      const commaIdx = raw.indexOf(",");
      const before = raw.slice(0, commaIdx).trim();
      const after = raw.slice(commaIdx + 1).trim();
      if (after) pushParts(after);
      if (before) pushParts(before);
    } else {
      pushParts(raw);
    }
  }

  return [...new Set(tokens)];
}

function genderToPronounKey(gender) {
  const g = String(gender || "").trim().toLowerCase();
  if (g === "male" || g === "m") return "male";
  if (g === "female" || g === "f") return "female";
  if (g === "other" || g === "non-binary" || g === "nonbinary") return "neutral";
  return null;
}

function inferPronounKeyFromToken(token) {
  const t = normalizeNameToken(token);
  if (!t) return null;
  if (MALE_FIRST_NAMES.has(t)) return "male";
  if (FEMALE_FIRST_NAMES.has(t)) return "female";
  return null;
}

function inferPronounKeyFromNameTokens(tokens) {
  if (!tokens.length) return null;

  const hits = tokens
    .map((token) => ({ token, key: inferPronounKeyFromToken(token) }))
    .filter((h) => h.key);

  if (!hits.length) return null;

  const hasMale = hits.some((h) => h.key === "male");
  const hasFemale = hits.some((h) => h.key === "female");

  if (hasMale && !hasFemale) return "male";
  if (hasFemale && !hasMale) return "female";

  return hits[hits.length - 1].key;
}

export function resolvePronounsFromPatient(input = {}) {
  const genderKey = genderToPronounKey(input.gender);
  if (genderKey) {
    return { ...PRONOUN_SETS[genderKey], pronounsSource: "gender" };
  }

  const nameTokens = parseNameTokensForInference(
    input.name,
    input.firstName,
    input.lastName
  );
  const nameKey = inferPronounKeyFromNameTokens(nameTokens);
  if (nameKey) {
    return { ...PRONOUN_SETS[nameKey], pronounsSource: "name" };
  }

  return { ...PRONOUN_SETS.male, pronounsSource: "default" };
}

export function attachPronounsToPatientPayload(payload) {
  const resolved = resolvePronounsFromPatient(payload);
  return { ...payload, ...resolved };
}

/** Choices for post-generation pronoun correction UI (chart rewrite dropdown) */
export const PRONOUN_CHOICE_OPTIONS = [
  {
    id: "he/him",
    label: "He / Him",
    dropdownLabel: "Him",
    shortLabel: "He",
    ...PRONOUN_SETS.male,
  },
  {
    id: "she/her",
    label: "She / Her",
    dropdownLabel: "Her",
    shortLabel: "She",
    ...PRONOUN_SETS.female,
  },
  {
    id: "they/them",
    label: "They / Them",
    dropdownLabel: "Them",
    shortLabel: "They",
    ...PRONOUN_SETS.neutral,
  },
];

export const PRONOUN_DROPDOWN_OPTIONS = PRONOUN_CHOICE_OPTIONS.map((opt) => ({
  value: opt.id,
  label: opt.dropdownLabel,
}));

/**
 * Infer Him vs Her from generated SOAP text (subjective/HPI narrative).
 * @returns {{ pronouns: 'he/him'|'she/her', source: 'soap_note', maleScore: number, femaleScore: number }|null}
 */
export function detectPronounsFromSoapText(text) {
  const raw = String(text || "").trim();
  if (!raw) return null;

  const t = raw.toLowerCase();
  let maleScore = 0;
  let femaleScore = 0;

  const countMatches = (regex) => {
    const m = t.match(regex);
    return m ? m.length : 0;
  };

  if (/\b\d+[- ]?year[- ]?old\s+male\b/.test(t) || /\bmale\s+patient\b/.test(t)) {
    maleScore += 8;
  }
  if (/\b\d+[- ]?year[- ]?old\s+female\b/.test(t) || /\bfemale\s+patient\b/.test(t)) {
    femaleScore += 8;
  }
  if (/\bhispanic\s+male\b/.test(t) || /\b\w+\s+male,?\s+presents\b/.test(t)) {
    maleScore += 4;
  }
  if (/\bhispanic\s+female\b/.test(t) || /\b\w+\s+female,?\s+presents\b/.test(t)) {
    femaleScore += 4;
  }

  maleScore += countMatches(/\bhe\b/g);
  maleScore += countMatches(/\bhim\b/g);
  maleScore += countMatches(/\bhis\b/g);

  femaleScore += countMatches(/\bshe\b/g);
  femaleScore += countMatches(/\bhers\b/g);
  femaleScore += countMatches(/\bher\b/g);

  if (maleScore === 0 && femaleScore === 0) return null;

  const pronouns =
    femaleScore > maleScore
      ? "she/her"
      : maleScore > femaleScore
        ? "he/him"
        : null;

  if (!pronouns) return null;

  return {
    pronouns,
    source: "soap_note",
    maleScore,
    femaleScore,
  };
}

export function detectPronounsFromSoapNote(soapNote) {
  if (!soapNote || typeof soapNote !== "object") return null;
  const parts = [
    soapNote.subjective,
    soapNote.objective,
    soapNote.assessment,
    soapNote.plan,
    soapNote.rawContent,
  ].filter((p) => p && String(p).trim());
  return detectPronounsFromSoapText(parts.join("\n\n"));
}

// --- NEW HELPER: Get the explicitly applied pronoun from a SOAP note ---
function getAppliedPronounFromSoapNote(soapNote) {
  if (!soapNote) return null;
  // Prefer direct metadata.pronouns (if we set it)
  if (soapNote.metadata?.pronouns) return soapNote.metadata.pronouns;
  // Fallback: backend stores the corrected pronoun inside magicEditorCorrection
  if (soapNote.metadata?.magicEditorCorrection?.pronouns) {
    return soapNote.metadata.magicEditorCorrection.pronouns;
  }
  return null;
}

// --- MODIFIED: include the applied pronoun in the cache key ---
export function soapContentKey(soapNote) {
  if (!soapNote) return "";
  const pronounValue = getAppliedPronounFromSoapNote(soapNote) || "";
  return [
    soapNote.soapNoteId,
    soapNote.version,
    soapNote.subjective,
    soapNote.objective,
    soapNote.assessment,
    soapNote.plan,
    soapNote.rawContent,
    pronounValue,
  ].join("|");
}

/**
 * Dropdown default: explicit correction first, then SOAP note text, then patient gender/name.
 */
export function resolveChartPronounsForDropdown(soapNote, patientContext) {
  // 1. Explicitly corrected pronoun (from magicEditorCorrection or metadata.pronouns)
  const appliedPronoun = getAppliedPronounFromSoapNote(soapNote);
  if (appliedPronoun === "he/him" || appliedPronoun === "she/her" || appliedPronoun === "they/them") {
    return {
      pronouns: appliedPronoun,
      source: "pronoun_metadata",
      maleScore: 0,
      femaleScore: 0,
    };
  }

  // 2. Detect from SOAP text
  // const fromSoap = detectPronounsFromSoapNote(soapNote);
  // if (fromSoap?.pronouns === "he/him" || fromSoap?.pronouns === "she/her") {
  //   return fromSoap;
  // }

  // 3. Patient gender / name inference
  // const fromPatient = resolvePronounsFromPatient(patientContext || {});
  // if (fromPatient.pronouns === "he/him" || fromPatient.pronouns === "she/her") {
  //   return {
  //     pronouns: fromPatient.pronouns,
  //     source: fromPatient.pronounsSource || "patient",
  //     maleScore: 0,
  //     femaleScore: 0,
  //   };
  // }
  

  // 2. No explicit pronoun – return null (show "+ add pronouns" in UI)
  return { pronouns: null, source: "none", maleScore: 0, femaleScore: 0 };

  // return { pronouns: "he/him", source: "default", maleScore: 0, femaleScore: 0 };
}
